import { useSyncExternalStore } from 'react';

export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

export interface ThemeState {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
}

const STORAGE_KEY = 'tradeos.theme';
const VALID_PREFERENCES: ThemePreference[] = ['dark', 'light', 'system'];

let preference: ThemePreference = 'dark';
let resolvedTheme: ResolvedTheme = 'dark';
let mediaQuery: MediaQueryList | null = null;
let mediaCleanup: (() => void) | null = null;
let snapshot: Pick<ThemeState, 'preference' | 'resolvedTheme'> = { preference, resolvedTheme };
const listeners = new Set<() => void>();

const isThemePreference = (value: string | null): value is ThemePreference =>
  !!value && VALID_PREFERENCES.includes(value as ThemePreference);

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const resolveTheme = (nextPreference: ThemePreference): ResolvedTheme =>
  nextPreference === 'system' ? getSystemTheme() : nextPreference;

const readStoredPreference = (): ThemePreference => {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'dark';
  } catch {
    return 'dark';
  }
};

const writeStoredPreference = (nextPreference: ThemePreference) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, nextPreference);
  } catch {
    // Preference persistence is best effort; theme still applies in-memory.
  }
};

const applyRootTheme = () => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.dataset.theme = resolvedTheme;
  document.documentElement.style.colorScheme = resolvedTheme;
};

const emit = () => {
  listeners.forEach((listener) => listener());
};

const updateSnapshot = () => {
  snapshot = { preference, resolvedTheme };
};

const syncTheme = () => {
  const nextResolvedTheme = resolveTheme(preference);
  const didChange = nextResolvedTheme !== resolvedTheme;
  resolvedTheme = nextResolvedTheme;
  updateSnapshot();
  applyRootTheme();
  if (didChange) {
    emit();
  }
};

const installSystemListener = () => {
  if (mediaCleanup || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return;
  }

  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = () => {
    if (preference === 'system') {
      syncTheme();
    }
  };

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleChange);
    mediaCleanup = () => mediaQuery?.removeEventListener('change', handleChange);
  } else {
    mediaQuery.addListener(handleChange);
    mediaCleanup = () => mediaQuery?.removeListener(handleChange);
  }
};

export const initTheme = () => {
  preference = readStoredPreference();
  resolvedTheme = resolveTheme(preference);
  updateSnapshot();
  applyRootTheme();
  installSystemListener();
};

export const getThemePreference = () => preference;

export const setThemePreference = (nextPreference: ThemePreference) => {
  if (!VALID_PREFERENCES.includes(nextPreference)) {
    return;
  }

  const didPreferenceChange = nextPreference !== preference;
  preference = nextPreference;
  writeStoredPreference(nextPreference);
  const nextResolvedTheme = resolveTheme(preference);
  const didThemeChange = nextResolvedTheme !== resolvedTheme;
  resolvedTheme = nextResolvedTheme;
  updateSnapshot();
  applyRootTheme();

  if (didPreferenceChange || didThemeChange) {
    emit();
  }
};

export const getThemeSnapshot = () => snapshot;

export const subscribeTheme = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useTheme = (): ThemeState => {
  const snapshot = useSyncExternalStore(subscribeTheme, getThemeSnapshot, getThemeSnapshot);

  return {
    ...snapshot,
    setPreference: setThemePreference,
  };
};
